import { ElementType, ReactNode } from 'react';
import classNames from 'classnames';

import { documentTypeIconsMap } from 'consts/documentIcons';
import { Classes } from 'types/Classes';
import Message from 'types/Message';
import { ProductDocumentTypeId } from 'types/models/ProductDocumentType';
import Button, { Props as ButtonProps } from '@components/atoms/Button';
import Typography from '@components/atoms/Typography';
import { FormattedMessage } from 'utils/intl';
import downloadFile from 'utils/downloadFile';

import classes from './FilePreview.module.scss';
import messages from './FilePreview.messages';

export type Props = {
  additionalButton?: (props: { classes: ButtonProps['classes'] }) => ReactNode;
  classes?: Classes<'root' | 'object' | 'embed'>;
  fileType: ProductDocumentTypeId;
  fileName: string;
  fileUrl: string;
  downloadFileName: string;
  downloadMessage?: Message;
  headerComponent?: ElementType;
  preview: ReactNode | ((props: { onDownload: () => Promise<void> }) => ReactNode);
  onClose?: () => void;
};

const FilePreview = ({
  additionalButton,
  classes: customClasses,
  fileType,
  fileName,
  fileUrl,
  downloadFileName,
  downloadMessage,
  headerComponent = 'div',
  preview,
}: Props): JSX.Element => {
  const IconComponent = documentTypeIconsMap[fileType];

  const buttonClasses = {
    root: classes.button,
    content: classes.buttonContent,
  };

  const onDownload = async () => {
    await downloadFile(fileUrl, downloadFileName);
  };

  return (
    <div className={customClasses?.root}>
      <div className={classes.title}>
        <div>
          <IconComponent height={24} />
        </div>
        <Typography variant="h2" renderAs={headerComponent} className={classes.name}>
          {fileName}
        </Typography>
      </div>
      {typeof preview === 'function' ? preview({ onDownload }) : preview}
      <div className={classes.buttons}>
        {additionalButton &&
          additionalButton({
            classes: { ...buttonClasses, root: classNames(buttonClasses.root, classes.cancelButton) },
          })}
        <Button
          color="tertiary"
          shape="round"
          size="lg"
          classes={{ ...buttonClasses, root: classNames(buttonClasses.root, classes.downloadButton) }}
          onClick={onDownload}
        >
          {downloadMessage || <FormattedMessage {...messages.download} />}
        </Button>
      </div>
    </div>
  );
};

export default FilePreview;
